import { App as AntApp } from 'antd';
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { helpdeskService } from '@/services/api';
import type { PendingCandidate, PickerSelectionMode } from '@/components/helpdesk/problemPickerCapture';
import { getUserFacingErrorMessage } from '@/utils/userFacingError';
import { useAuth } from '@/contexts/useAuth';
import { readActiveContour } from '@/utils/activeContour';
import { readActiveWorkspace } from '@/utils/activeWorkspace';
import { buildAccessEnvelopeFingerprint } from '@/utils/authBootstrap';
import { stripHelpdeskOwnershipSearch } from '@/utils/helpdeskOwnership';
import type { HelpdeskDraft, HelpdeskThreadDetail } from '@/types';

export type ProblemPickerFlow = 'legacy' | 'quick';
export type ProblemPickerStep = 'select' | 'describe' | 'submitting' | 'submitted';

type ProblemPickerContextValue = {
  draft: HelpdeskDraft | null;
  draftId: number | null;
  pickerEnabled: boolean;
  panelVisible: boolean;
  pendingCandidate: PendingCandidate | null;
  pendingCandidateResolved: boolean;
  pendingComment: string;
  loading: boolean;
  flow: ProblemPickerFlow;
  step: ProblemPickerStep;
  selectionMode: PickerSelectionMode;
  description: string;
  submittedThread: HelpdeskThreadDetail | null;
  ensureDraft: (payload?: {
    topic?: string;
    category?: string;
    description?: string;
    source?: 'manual' | 'bug_report';
    metadata?: Record<string, unknown>;
    draftId?: number | null;
  }) => Promise<HelpdeskDraft | null>;
  hydrateDraft: (draftId: number) => Promise<HelpdeskDraft | null>;
  saveDraft: (payload: {
    topic?: string | null;
    category?: string | null;
    description?: string | null;
    source?: 'manual' | 'bug_report' | null;
    status?: string | null;
    metadata?: Record<string, unknown> | null;
  }) => Promise<HelpdeskDraft | null>;
  openPicker: (options?: { draftId?: number | null; returnUrl?: string | null }) => void;
  startQuickReport: () => Promise<HelpdeskDraft | null>;
  closePickerPanel: () => void;
  setPickerEnabled: (value: boolean) => void;
  setPendingCandidate: (candidate: PendingCandidate | null) => void;
  setPendingCandidateResolved: (value: boolean) => void;
  setPendingComment: (value: string) => void;
  setSelectionMode: (value: PickerSelectionMode) => void;
  setDescription: (value: string) => void;
  addPendingAnchor: () => Promise<HelpdeskDraft | null>;
  removeAnchor: (anchorId: number) => Promise<HelpdeskDraft | null>;
  finishCollection: () => void;
  continueToDescription: () => void;
  returnToSelection: () => void;
  submitQuickReport: () => Promise<HelpdeskThreadDetail | null>;
  openSubmittedThread: () => void;
  clearSession: () => void;
};

const ProblemPickerContext = createContext<ProblemPickerContextValue | null>(null);

const readProblemPickerScopeKey = (): string => (
  `${readActiveContour() ?? '-'}|${readActiveWorkspace() ?? '-'}`
);

export const ProblemPickerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = AntApp.useApp();
  const { user, accessPolicy, entryContext, loading: authLoading } = useAuth();
  const [draft, setDraft] = useState<HelpdeskDraft | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [pickerEnabled, setPickerEnabled] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [pendingCandidate, setPendingCandidate] = useState<PendingCandidate | null>(null);
  const [pendingCandidateResolved, setPendingCandidateResolved] = useState(false);
  const [pendingComment, setPendingComment] = useState('');
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [flow, setFlow] = useState<ProblemPickerFlow>('legacy');
  const [step, setStep] = useState<ProblemPickerStep>('select');
  const [selectionMode, setSelectionMode] = useState<PickerSelectionMode>('element');
  const [description, setDescription] = useState('');
  const [submittedThread, setSubmittedThread] = useState<HelpdeskThreadDetail | null>(null);
  const [scopeKey, setScopeKey] = useState(readProblemPickerScopeKey);
  const autoOpenSignatureRef = useRef<string | null>(null);
  const requestEpochRef = useRef(0);
  const ownershipKey = `${authLoading ? 'loading' : 'ready'}|${buildAccessEnvelopeFingerprint(
    user,
    accessPolicy,
    entryContext,
  )}|${scopeKey}`;
  const ownershipKeyRef = useRef(ownershipKey);
  const [stateOwnershipKey, setStateOwnershipKey] = useState(ownershipKey);
  const [urlOwnershipKey, setUrlOwnershipKey] = useState(ownershipKey);
  if (ownershipKeyRef.current !== ownershipKey) {
    ownershipKeyRef.current = ownershipKey;
    requestEpochRef.current += 1;
  }

  const renderEpoch = requestEpochRef.current;
  const renderOwnsCurrentEpoch = (): boolean => (
    requestEpochRef.current === renderEpoch && ownershipKeyRef.current === ownershipKey
  );
  const beginOwnedRequest = (): number | null => renderOwnsCurrentEpoch() ? renderEpoch : null;
  const requestIsCurrent = (epoch: number): boolean => requestEpochRef.current === epoch;

  const resetTransientSelection = () => {
    setPendingCandidate(null);
    setPendingCandidateResolved(false);
    setPendingComment('');
  };

  useEffect(() => {
    const handleScopeChange = () => setScopeKey(readProblemPickerScopeKey());
    window.addEventListener('active_contour_changed', handleScopeChange);
    window.addEventListener('active_workspace_changed', handleScopeChange);
    return () => {
      window.removeEventListener('active_contour_changed', handleScopeChange);
      window.removeEventListener('active_workspace_changed', handleScopeChange);
    };
  }, []);

  useLayoutEffect(() => {
    setDraft(null);
    setDraftId(null);
    setPickerEnabled(false);
    setPanelVisible(false);
    setPendingCandidate(null);
    setPendingCandidateResolved(false);
    setPendingComment('');
    setReturnUrl(null);
    setLoading(false);
    setFlow('legacy');
    setStep('select');
    setSelectionMode('element');
    setDescription('');
    setSubmittedThread(null);
    setStateOwnershipKey(ownershipKey);
    autoOpenSignatureRef.current = null;
  }, [ownershipKey]);

  useLayoutEffect(() => {
    if (urlOwnershipKey === ownershipKey) return;
    const cleanSearch = stripHelpdeskOwnershipSearch(location.search);
    if (cleanSearch !== location.search) {
      navigate({ pathname: location.pathname, search: cleanSearch, hash: location.hash }, { replace: true });
      return;
    }
    setUrlOwnershipKey(ownershipKey);
  }, [location.hash, location.pathname, location.search, navigate, ownershipKey, urlOwnershipKey]);

  const hydrateDraft = async (nextDraftId: number): Promise<HelpdeskDraft | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    setLoading(true);
    try {
      const loaded = await helpdeskService.getDraft(nextDraftId);
      if (requestIsCurrent(requestEpoch)) {
        setDraft(loaded);
        setDraftId(loaded.id);
        return loaded;
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const ensureDraft = async (payload?: {
    topic?: string;
    category?: string;
    description?: string;
    source?: 'manual' | 'bug_report';
    metadata?: Record<string, unknown>;
    draftId?: number | null;
  }): Promise<HelpdeskDraft | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    const requestedDraftId = payload?.draftId ?? null;
    if (requestedDraftId != null) {
      if (stateOwnershipKey === ownershipKey && draft?.id === requestedDraftId) return draft;
      return hydrateDraft(requestedDraftId);
    }
    if (
      stateOwnershipKey === ownershipKey
      && draft
      && draft.status === 'collecting'
      && (!payload?.source || draft.source === payload.source)
      && (!payload?.category || draft.category === payload.category)
    ) return draft;
    setLoading(true);
    try {
      const created = await helpdeskService.createDraft({
        topic: payload?.topic,
        category: payload?.category,
        description: payload?.description,
        source: payload?.source,
        metadata: payload?.metadata,
      });
      if (requestIsCurrent(requestEpoch)) {
        setDraft(created);
        setDraftId(created.id);
        return created;
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const saveDraft = async (payload: {
    topic?: string | null;
    category?: string | null;
    description?: string | null;
    source?: 'manual' | 'bug_report' | null;
    status?: string | null;
    metadata?: Record<string, unknown> | null;
  }): Promise<HelpdeskDraft | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    if (draftId == null) return null;
    setLoading(true);
    try {
      const updated = await helpdeskService.updateDraft(draftId, payload);
      if (requestIsCurrent(requestEpoch)) {
        setDraft(updated);
        return updated;
      }
      return null;
    } catch (error) {
      if (requestIsCurrent(requestEpoch)) {
        message.error(getUserFacingErrorMessage(error, { fallbackMessage: 'Не удалось сохранить черновик.' }));
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const openPicker = (options?: { draftId?: number | null; returnUrl?: string | null }) => {
    if (!renderOwnsCurrentEpoch()) return;
    setFlow('legacy');
    setStep('select');
    setSelectionMode('element');
    setSubmittedThread(null);
    if (options?.draftId != null) setDraftId(options.draftId);
    setReturnUrl(options?.returnUrl ?? null);
    setPanelVisible(true);
    setPickerEnabled(true);
  };

  const startQuickReport = async (): Promise<HelpdeskDraft | null> => {
    if (!renderOwnsCurrentEpoch()) return null;
    requestEpochRef.current += 1;
    const requestEpoch = requestEpochRef.current;
    setFlow('quick');
    setStep('select');
    setSelectionMode('element');
    setDescription('');
    setSubmittedThread(null);
    setReturnUrl(null);
    setDraft(null);
    setDraftId(null);
    resetTransientSelection();
    setPanelVisible(true);
    setPickerEnabled(true);
    setLoading(true);
    try {
      const created = await helpdeskService.createDraft({
        category: 'bug',
        source: 'bug_report',
        metadata: {
          quick_report: true,
          launch_route: window.location.pathname,
          build_sha: import.meta.env.VITE_BUILD_SHA ?? null,
          build_time_utc: import.meta.env.VITE_BUILD_TIME_UTC ?? null,
        },
      });
      if (requestIsCurrent(requestEpoch)) {
        setDraft(created);
        setDraftId(created.id);
        return created;
      }
      return null;
    } catch (error) {
      if (requestIsCurrent(requestEpoch)) {
        setPanelVisible(false);
        setPickerEnabled(false);
        message.error(getUserFacingErrorMessage(error, { fallbackMessage: 'Не удалось открыть форму обращения.' }));
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const closePickerPanel = () => {
    if (!renderOwnsCurrentEpoch()) return;
    setPanelVisible(false);
    setPickerEnabled(false);
    resetTransientSelection();
    setStep('select');
    setDescription('');
    setSubmittedThread(null);
  };

  const addPendingAnchor = async (): Promise<HelpdeskDraft | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    if (draftId == null || !pendingCandidate) return null;
    setLoading(true);
    try {
      const response = await helpdeskService.createAnchor(draftId, {
        ...pendingCandidate,
        comment: pendingComment.trim() || undefined,
      });
      if (requestIsCurrent(requestEpoch)) {
        setDraft(response.draft);
        resetTransientSelection();
        setPickerEnabled(false);
        message.success('Участок добавлен');
        return response.draft;
      }
      return null;
    } catch (error) {
      if (requestIsCurrent(requestEpoch)) {
        message.error(getUserFacingErrorMessage(error, { fallbackMessage: 'Не удалось сохранить участок.' }));
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const removeAnchor = async (anchorId: number): Promise<HelpdeskDraft | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    if (draftId == null) return null;
    setLoading(true);
    try {
      const updated = await helpdeskService.deleteAnchor(draftId, anchorId);
      if (requestIsCurrent(requestEpoch)) {
        setDraft(updated);
        return updated;
      }
      return null;
    } catch (error) {
      if (requestIsCurrent(requestEpoch)) {
        message.error(getUserFacingErrorMessage(error, { fallbackMessage: 'Не удалось удалить участок.' }));
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const finishCollection = () => {
    if (!renderOwnsCurrentEpoch()) return;
    setPickerEnabled(false);
    setPanelVisible(false);
    resetTransientSelection();
    if (returnUrl) navigate(returnUrl);
  };

  const continueToDescription = () => {
    if (!renderOwnsCurrentEpoch()) return;
    if (!draft?.anchors.length || pendingCandidate) return;
    setPickerEnabled(false);
    setStep('describe');
  };

  const returnToSelection = () => {
    if (!renderOwnsCurrentEpoch()) return;
    setStep('select');
    setPickerEnabled(false);
  };

  const submitQuickReport = async (): Promise<HelpdeskThreadDetail | null> => {
    const requestEpoch = beginOwnedRequest();
    if (requestEpoch == null) return null;
    if (draftId == null || !draft?.anchors.length || description.trim().length < 3) return null;
    setStep('submitting');
    setLoading(true);
    try {
      const created = await helpdeskService.submitQuickReport({ draft_id: draftId, description: description.trim() });
      if (requestIsCurrent(requestEpoch)) {
        setSubmittedThread(created);
        setDraft((current) => current ? { ...current, status: 'submitted', description: description.trim() } : current);
        setStep('submitted');
        return created;
      }
      return null;
    } catch (error) {
      if (requestIsCurrent(requestEpoch)) {
        setStep('describe');
        message.error(getUserFacingErrorMessage(error, { fallbackMessage: 'Не удалось отправить обращение.' }));
      }
      return null;
    } finally {
      if (requestIsCurrent(requestEpoch)) setLoading(false);
    }
  };

  const openSubmittedThread = () => {
    if (!renderOwnsCurrentEpoch()) return;
    if (!submittedThread) return;
    const threadId = submittedThread.id;
    closePickerPanel();
    navigate(`/helpdesk?threadId=${threadId}`);
  };

  const clearSession = () => {
    if (!renderOwnsCurrentEpoch()) return;
    requestEpochRef.current += 1;
    setDraft(null);
    setDraftId(null);
    setPanelVisible(false);
    setPickerEnabled(false);
    resetTransientSelection();
    setReturnUrl(null);
    setFlow('legacy');
    setStep('select');
    setSelectionMode('element');
    setDescription('');
    setSubmittedThread(null);
  };

  useEffect(() => {
    if (authLoading || !user || urlOwnershipKey !== ownershipKey) return;
    const params = new URLSearchParams(location.search);
    if (params.get('problemPicker') !== '1') return;
    const draftIdParam = Number(params.get('draftId') || '');
    if (!Number.isFinite(draftIdParam) || draftIdParam <= 0) return;
    const signature = `${location.pathname}?${location.search}`;
    if (autoOpenSignatureRef.current === signature) return;
    autoOpenSignatureRef.current = signature;
    setFlow('legacy');
    setStep('select');
    setDraftId(draftIdParam);
    setReturnUrl(params.get('problemPickerReturnUrl'));
    setPanelVisible(true);
    setPickerEnabled(true);
    setLoading(true);
    const requestEpoch = requestEpochRef.current;
    void helpdeskService.getDraft(draftIdParam)
      .then((loaded) => {
        if (requestIsCurrent(requestEpoch)) {
          setDraft(loaded);
          setDraftId(loaded.id);
        }
      })
      .catch(() => {
        if (requestIsCurrent(requestEpoch)) message.error('Не удалось открыть черновик');
      })
      .finally(() => {
        if (requestIsCurrent(requestEpoch)) setLoading(false);
      });
  }, [authLoading, location.pathname, location.search, message, ownershipKey, urlOwnershipKey, user]);

  const ownsVisibleState = stateOwnershipKey === ownershipKey;
  const value: ProblemPickerContextValue = {
    draft: ownsVisibleState ? draft : null,
    draftId: ownsVisibleState ? draftId : null,
    pickerEnabled: ownsVisibleState ? pickerEnabled : false,
    panelVisible: ownsVisibleState ? panelVisible : false,
    pendingCandidate: ownsVisibleState ? pendingCandidate : null,
    pendingCandidateResolved: ownsVisibleState ? pendingCandidateResolved : false,
    pendingComment: ownsVisibleState ? pendingComment : '',
    loading: ownsVisibleState ? loading : false,
    flow: ownsVisibleState ? flow : 'legacy',
    step: ownsVisibleState ? step : 'select',
    selectionMode: ownsVisibleState ? selectionMode : 'element',
    description: ownsVisibleState ? description : '',
    submittedThread: ownsVisibleState ? submittedThread : null,
    ensureDraft,
    hydrateDraft,
    saveDraft,
    openPicker,
    startQuickReport,
    closePickerPanel,
    setPickerEnabled: (next) => { if (renderOwnsCurrentEpoch()) setPickerEnabled(next); },
    setPendingCandidate: (next) => { if (renderOwnsCurrentEpoch()) setPendingCandidate(next); },
    setPendingCandidateResolved: (next) => { if (renderOwnsCurrentEpoch()) setPendingCandidateResolved(next); },
    setPendingComment: (next) => { if (renderOwnsCurrentEpoch()) setPendingComment(next); },
    setSelectionMode: (next) => { if (renderOwnsCurrentEpoch()) setSelectionMode(next); },
    setDescription: (next) => { if (renderOwnsCurrentEpoch()) setDescription(next); },
    addPendingAnchor,
    removeAnchor,
    finishCollection,
    continueToDescription,
    returnToSelection,
    submitQuickReport,
    openSubmittedThread,
    clearSession,
  };

  return <ProblemPickerContext.Provider value={value}>{children}</ProblemPickerContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useProblemPicker = (): ProblemPickerContextValue => {
  const context = useContext(ProblemPickerContext);
  if (!context) throw new Error('useProblemPicker must be used within ProblemPickerProvider');
  return context;
};
